import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators'
import { AssetFile, BoundingBox, Point, SingleAssetUsage } from './asset'
import { store } from '@/store'
import { AppModule } from '@/store/modules/app'
import { ImageRatio, PaintingByNumbers } from '@/rest/models'
import { AddPaintingByNumbersRequest } from '@/rest/apis'
import { Design, Category, Collage } from '@/rest'
import Vue from 'vue'
import i18n from '@/i18n'
import { getProductId }from '@/components/products/malennachzahlen/util/partsUtil'

export interface PaintingByNumbersState {
    title: string
    userInputTitle: string
    boxDesign: number
    parts: string
    giftOption: boolean
    previewAssetId: string
}

export class PaintingInfo {
    assetId: number
    boundingBox: { ll: Point, ur: Point }
    rotation: number
    attributesNeedUpdate: boolean

    constructor(
        assetId: number, 
        boundingBox: { ll: Point, ur: Point },
        rotation: number,
        attributesNeedUpdate: boolean) {
            this.assetId = assetId
            this.boundingBox = boundingBox
            this.rotation = rotation
            this.attributesNeedUpdate = attributesNeedUpdate
    }

    attributesChanged(newVersion: PaintingInfo): boolean {
        if(this.assetId != newVersion.assetId) {
            return true
        }
        if(this.boundingBox.ll[0] != newVersion.boundingBox.ll[0] || this.boundingBox.ll[1] != newVersion.boundingBox.ll[1]) {
            return true
        }
        if(this.boundingBox.ur[0] != newVersion.boundingBox.ur[0] || this.boundingBox.ur[1] != newVersion.boundingBox.ur[1]) {
            return true
        }
        if(this.rotation != newVersion.rotation) {
            return true
        }
        return false
    }
}

export enum PaintingFormat {
    LANDSCAPE,
    PORTRAIT
}

export enum PaintingBoxUpdateState {
    UPDATED,
    UPDATING_ASSET_ATTRIBUTES,
    UPDATING_BOX_ASSET
}

export type PaintingUsageAndInfo = {
    [key in string]: PaintingInfo
}

export type ActiveAsset = {
    [key in string]: AssetFile
}

export type ProductName = {
    [key in string]: string
}

export type NameAndUsage = {
    [key in string]: SingleAssetUsage
}

export class PaintingByNumbersTransfer implements PaintingByNumbers {

    public sessionId?: string
    public title: string
    public design: number
    public boxType: string
    public parts: string
    public format: string
    public dru1AssetId: number
    public gift?: boolean
    
    constructor(sessionId: string | undefined, title: string, parts: string, design: number,
            format: string, dru1AssetId: number) {
        this.title = title
        this.sessionId = sessionId
        this.parts = parts
        this.format = format
        this.dru1AssetId = dru1AssetId
        this.design = design
        this.boxType = "METAL_BOX"
    }
}

@Module({ dynamic: true, store, name: 'paintingByNumbers' })
class PaintingByNumbersImpl extends VuexModule implements PaintingByNumbersState {
    public title = ""
    public userInputTitle = ""
    public parts = ''
    public boxDesign = 1
    public boxDesignCategory = 0
    public giftOption = false
    public format = PaintingFormat.LANDSCAPE
    public userFormat = ''
    public overlayUploadVisibe = false
    public availableParts: { value: string; text: string; }[] = []
    public availableCategories: Category[] = []
    public availableDesigns: Array<Array<Design>> = []
    public designsNotEmpty: number[] = []
    public productNames: ProductName = {
                        '100000': i18n.t('paintingSquare').toString() + ' (20 x 20cm)',
                        '100001': i18n.t('paintingPoster').toString() + ' (30 x 24cm)'}
    public paintingUsageAndInfo: PaintingUsageAndInfo = {}
    public activeAssets: ActiveAsset = {}
    public imageRatios: Map<string, ImageRatio> = new Map()
    public maxCanvasSize = 400
    public croppingBorderInMm = 5
    public previewAssetId = ''
    public recoverPaintingToggled = false
    public persistedAssets: ActiveAsset = {}
    public persistedUsages: NameAndUsage = {}
    public persistedInfos: PaintingUsageAndInfo = {}
    public stepsReseted = false
    public paintingBoxUpdateState: PaintingBoxUpdateState = PaintingBoxUpdateState.UPDATED
    public deletingAsset = false
    public resetBoxDesignToggled = false
    public maxAssetsAmount = 100
    public updatedCategories = false
    public updatePreview = false

    @Mutation
    public SET_TITLE(title: string) {
        this.title = title
    }

    @Mutation
    public SET_USER_INPUT_TITLE(userInputTitle: string) {
        this.userInputTitle = userInputTitle
    }

    @Mutation
    public SET_PARTS(parts: string) {
        let productId = getProductId(parts)
        if (productId)
            AppModule.SetProductID(productId)
        this.parts = parts
    }

    @Mutation
    public SET_GIFT_OPTION(giftOption: boolean) {
        this.giftOption = giftOption
    }

    @Mutation
    private SET_PAINTING_FORMAT(format: PaintingFormat){
        this.format = format
    }

    @Mutation
    private SET_USER_FORMAT(format: string){
        this.userFormat = format
    }

    @Mutation
    private SET_BOX_DESIGN(boxDesign: number){
        this.boxDesign = boxDesign
    }

    @Mutation
    private SET_BOX_DESIGN_CATEGORY(boxDesignCategory: number){
        this.boxDesignCategory = boxDesignCategory
    }

    @Mutation
    private SET_RECOVER_PAINTING_TOGGLED(toggled: boolean) {
        this.recoverPaintingToggled = toggled
    }

    @Mutation
    private SET_SHOW_OVERLAY_UPLOAD(showUpload: boolean) {
        this.overlayUploadVisibe = showUpload
    }

    @Mutation
    private SET_PAINTING_USAGE_INFO(info: PaintingInfo) {
        let usageName = 'paintingimage'
        this.paintingUsageAndInfo[usageName] = info
    }

    @Mutation
    private SET_PAINTING_USAGE_AND_INFO([usage, info]: [string, PaintingInfo]) {
        this.paintingUsageAndInfo[usage] = info
    }

    @Mutation
    private PERSIST_USAGE_AND_INFO([usage, info]: [string, PaintingInfo]) {
        this.persistedInfos[usage] = info
    }

    @Mutation
    private SET_NAME_AND_USAGE([usagename, usage]: [string, SingleAssetUsage]) {
        this.persistedUsages[usagename] = usage
    }

    @Mutation
    private SET_ACTIVE_ASSET([usage, asset]: [string, AssetFile]) {
        this.activeAssets[usage] = asset
        this.updatePreview = !this.updatePreview
    }

    @Mutation
    private SET_PERSISTED_ASSET([usage, asset]: [string, AssetFile]) {
        this.persistedAssets[usage] = asset
    }

    @Mutation
    private UNSET_ACTIVE_ASSET(usage: string) {
        Vue.delete(this.activeAssets, usage)
    }

    @Mutation
    private SET_AVAILABLE_PARTS(parts: string[]) {
        for (let i = 0; i < parts.length; i++){
            let newValue = parts[i]
            let newText = this.productNames[parts[i]]
            this.availableParts.push({value: newValue, text: newText})
        }
    }

    @Mutation
    public SET_AVAILABLE_BOX_CATEGORIES(categories: Category[]) {
        this.availableCategories = categories
    }

    @Mutation
    public SET_AVAILABLE_DESIGNS(categoriesAndDesigns: Design[]) {
        this.availableDesigns.push(categoriesAndDesigns)
    }

    @Mutation
    public SET_DESIGN_NOT_EMPTY(id: number) {
        this.designsNotEmpty.push(id)
    }

    @Mutation
    public CLEAR_AVAILABLE_BOX_CATEGORIES(clear: boolean) {
        this.availableCategories = []
    }

    @Mutation
    public CLEAR_AVAILABLE_DESIGNS(clear: boolean) {
        this.availableDesigns = []
    }

    @Mutation
    public CLEAR_DESIGN_NOT_EMPTY(clear: boolean) {
        this.designsNotEmpty = []
    }

    @Mutation
    public SET_IMAGE_RATIOS(ratios: Map<string, ImageRatio>) {
        this.imageRatios = ratios
    }

    @Mutation
    public SET_PREVIEW_ASSET_ID(previewAssetId: string) {
        this.previewAssetId = previewAssetId
    }

    @Mutation
    public SET_STEPS_RESETED(stepsReseted: boolean) {
        this.stepsReseted = stepsReseted
    }

    @Mutation
    public CLEAR_PERSISTED_ASSET_DATA(key: string) {
        Vue.delete(this.persistedAssets, key)
        Vue.delete(this.persistedUsages, key)
        Vue.delete(this.persistedInfos, key)
    }

    @Mutation
    public CLEAR_ALL_COLLAGES(key: string) {
        Vue.delete(this.paintingUsageAndInfo, key)
    }

    @Mutation
    public PAINTING_BOX_UPDATE_STATE(paintingBoxUpdateState: PaintingBoxUpdateState) {
        this.paintingBoxUpdateState = paintingBoxUpdateState
    }

    @Mutation
    public SET_DELETING_ASSET(deletingAsset: boolean) {
        this.deletingAsset = deletingAsset
    }

    @Mutation
    private SET_RESET_BOX_DESIGN_TOGGLED(toggled: boolean) {
        this.resetBoxDesignToggled = toggled
    }

    @Mutation
    private SET_UPDATED_CATEGORIES(updatedCategories: boolean) {
        this.updatedCategories = updatedCategories
    }

    @Action({ commit: 'SET_TITLE' })
    public SetTitle(title: string) {
        return title
    }

    @Action({ commit: 'SET_USER_INPUT_TITLE' })
    public SetUserInputTitle(title: string) {
        return title
    }

    @Action({ commit: 'SET_PARTS' })
    public SetParts(parts: string) {
        return parts
    }

    @Action({ commit: 'SET_GIFT_OPTION' })
    public SetGiftOption(giftOption: boolean) {
        return giftOption
    }

    @Action({ commit: 'SET_PAINTING_FORMAT' })
    public SetPaintingFormat(format: PaintingFormat) {
        return format
    }

    @Action({ commit: 'SET_USER_FORMAT' })
    public SetUserFormat(format: string) {
        return format
    }

    @Action({ commit: 'SET_BOX_DESIGN' })
    public SetBoxDesign(boxDesign: number) {
        return boxDesign
    }

    @Action({ commit: 'SET_BOX_DESIGN_CATEGORY' })
    public SetBoxDesignCategory(boxDesignCategory: number) {
        return boxDesignCategory
    }

    @Action({ commit: 'SET_RECOVER_PAINTING_TOGGLED' })
    public SetRecoverPaintingToggled(toggled: boolean) {
        return toggled
    }

    @Action({ commit: 'SET_SHOW_OVERLAY_UPLOAD' })
    public SetShowOverlayUpload(showUpload: boolean) {
        return showUpload
    }

    @Action({ commit: 'SET_PAINTING_USAGE_INFO' })
    public SetPaintingInfo(info: PaintingInfo) {
        return info
    }

    @Action({ commit: 'SET_PAINTING_USAGE_AND_INFO' })
    public SetPaintingUsageAndInfo([usage, info]: [string, PaintingInfo]) {
        return [usage, info]
    }

    @Action({ commit: 'PERSIST_USAGE_AND_INFO' })
    public PersistPaintingUsageAndInfo([usage, info]: [string, PaintingInfo]) {
        return [usage, info]
    }

    @Action({ commit: 'SET_NAME_AND_USAGE' })
    public PersistNameAndUsage([usagename, usage]: [string, SingleAssetUsage]) {
        return [usagename, usage]
    }

    @Action({ commit: 'SET_ACTIVE_ASSET' })
    public SetActiveAsset(asset: [String, AssetFile]) {
        return asset
    }

    @Action({ commit: 'UNSET_ACTIVE_ASSET' })
    public UnsetActiveAsset(usageName: String) {
        return usageName
    }

    @Action({ commit: 'SET_PERSISTED_ASSET' })
    public SetPersistedAsset(asset: [String, AssetFile]) {
        return asset
    }

    @Action({ commit: 'SET_AVAILABLE_PARTS' })
    public SetAvailableParts(parts: string[]) {
        return parts
    }

    @Action({ commit: 'SET_AVAILABLE_BOX_CATEGORIES' })
    public SetAvailableBoxCategories(categories: Category[]) {
        return categories
    }

    @Action({ commit: 'SET_AVAILABLE_DESIGNS' })
    public SetAvailableDesigns (categoriesAndDesigns: Design[]) {
        return categoriesAndDesigns
    }

    @Action({ commit: 'SET_DESIGN_NOT_EMPTY' })
    public SetDesignNotEmpty(id: number) {
        return id
    }

    @Action({ commit: 'CLEAR_AVAILABLE_BOX_CATEGORIES' })
    public ClearCategories(clear: boolean) {
        return clear
    }

    @Action({ commit: 'CLEAR_AVAILABLE_DESIGNS' })
    public ClearDesigns(clear: boolean) {
        return clear
    }

    @Action({ commit: 'CLEAR_DESIGN_NOT_EMPTY' })
    public ClearNotEmptyDesigns(clear: boolean) {
        return clear
    }

    @Action({ commit: 'SET_IMAGE_RATIOS' })
    public SetImageRatios(ratios: Map<string, ImageRatio>) {
        return ratios
    }

    @Action({ commit: 'SET_PREVIEW_ASSET_ID' })
    public SetPreviewAssetId(previewAssetId: string) {
        return previewAssetId
    }

    @Action({ commit: 'SET_STEPS_RESETED' })
    public SetStepsReseted(stepsReseted: boolean) {
        return stepsReseted
    }

    @Action({ commit: 'CLEAR_PERSISTED_ASSET_DATA' })
    public ClearPersistedAssetData(key: string) {
        return key
    }

    @Action({ commit: 'CLEAR_ALL_COLLAGES' })
    public ClearAllCollages(key: string) {
        return key
    }

    @Action({ commit: 'PAINTING_BOX_UPDATE_STATE' })
    public SetPaintingBoxUpdateState(paintingBoxUpdateState: PaintingBoxUpdateState) {
        return paintingBoxUpdateState
    }

    @Action({ commit: 'SET_DELETING_ASSET' })
    public SetDeletingAsset(deletingAsset: boolean) {
        return deletingAsset
    }

    @Action({ commit: 'SET_RESET_BOX_DESIGN_TOGGLED' })
    public SetResetBoxDesignToggled(resetBoxToggled: boolean) {
        return resetBoxToggled
    }

    @Action({ commit: 'SET_UPDATED_CATEGORIES' })
    public setUpdatedCategories(updatedCategories: boolean) {
        return updatedCategories
    }

    get showBox(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "design-box-metal" && !AppModule.showMenu
    }

    get showTitle(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "enter-title" && !AppModule.showMenu
    }

    get showPainting(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "design-painting" && !AppModule.showMenu
    }

    get showImageUpload(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "painting-image-upload"
    }

    get showImagePreview(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "painting-image-upload" && !AppModule.showMenu
    }

    get showBoxEdit(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "design-box-metal" || step.componentName === "enter-title" || (AppModule.showMenu  && this.stepThreeCompleted)
    }

    get stepThreeCompleted(): boolean {
        return AppModule.steps[2].completed
    }

    get paintingByNumbersTransfer() :AddPaintingByNumbersRequest {
        let paintingByNumbers = new PaintingByNumbersTransfer(AppModule.session.sessionId, PaintingByNumbersModule.title, PaintingByNumbersModule.parts,
            PaintingByNumbersModule.boxDesign, PaintingFormat[PaintingByNumbersModule.format], +this.previewAssetId)

            paintingByNumbers.gift = PaintingByNumbersModule.giftOption

        let addPaintingByNumbersRequest: AddPaintingByNumbersRequest = {paintingByNumbers: paintingByNumbers}
        return addPaintingByNumbersRequest;
    }

    get isLandscapeFormat() : boolean {
        return this.format == PaintingFormat.LANDSCAPE
    }

    get showToolbar(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "design-painting" && !AppModule.showMenu
    }

    get imageEditorWidth() {
        let ratio = this.imageRatios.get(this.parts)
        if (this.format === PaintingFormat.LANDSCAPE) {
            return this.maxCanvasSize
        } else if (ratio && ratio.width && ratio.height){
            let scale  = this.maxCanvasSize / ratio.width
            return Math.round(ratio.height * scale)
        }
        return 0
    }

    get imageEditorHeight() {
        let ratio = this.imageRatios.get(this.parts)
        if (this.format !== PaintingFormat.LANDSCAPE) {
            return this.maxCanvasSize
        } else if (ratio && ratio.width && ratio.height){
            let scale  = this.maxCanvasSize / ratio.width
            return Math.round(ratio.height * scale)
        }
        return 0
    }

    get showCollageUpload(){
        return this.overlayUploadVisibe
    }

    get numberOfImages() {
        return 1
    }
}

export const PaintingByNumbersModule = getModule(PaintingByNumbersImpl)
